import { Outlet } from 'react-router-dom'
import { useLocation } from 'react-router-dom'
import { m, LazyMotion, domAnimation } from 'framer-motion'
import { Suspense, useMemo } from 'react'

import { useTenant } from '@/lib/provider/TenantProvider'
import { useApp } from '@/lib/provider/AppProvider'
import FeedbackIcon from '@icons/chat-bubble-left-right'
import FeedbackPanel from '@/components/FeedbackPanel'
import { FeedbackProvider, useFeedback } from '@/lib/provider/FeedbackProvider'
import KnowledgeBasePanel from '@/components/knowledge-base/KnowledgeBasePanel'
import {
  KnowledgeBaseProvider,
  useKnowledgeBase,
  useKnowledgeBaseDockedWidth,
} from '@/lib/provider/KnowledgeBaseProvider'

import Navigation from '@/layouts/partials/Navigation'
import Sidebar from '@/layouts/partials/Sidebar'
import CustomerBanner from '@/components/CustomerBanner'
import Footer from '@/layouts/partials/Footer'
import AnalyticsBox from '@/components/AnalyticsBox'
import EmailVerificationBanner from '@/components/EmailVerificationBanner'
import { useAuth } from '@/lib/provider/AuthProvider'
import XMarkIcon from '@icons/x-mark'
import { cn } from '@/lib/misc/Utils'
import { FLAGS, useFeatureFlag } from '@/lib/hooks/useFeatureFlag'
import { useNavLayout } from '@/lib/hooks/useNavLayout'
import { useCompanySelection } from '@/lib/hooks/useCompanySelection'
import { useCompanyInvitations } from '@/lib/hooks/useCompanyInvitations'
import CompanyChooser from '@/layouts/partials/CompanyChooser'
import CompanyInvitations from '@/layouts/partials/CompanyInvitations'
import TwoFactorEnforcementDialog from '@/components/TwoFactorEnforcementDialog'
import { createPortal } from 'react-dom'
import AddService from '@/pages/dashboard/partials/AddService'

const pageVariants = {
  initial: {
    opacity: 0,
    y: 4,
  },
  in: {
    opacity: 1,
    y: 0,
  },
  out: {
    opacity: 0,
    y: -2,
  },
}

const pageTransition = {
  type: 'tween' as const,
  ease: [0.32, 0.72, 0, 1] as const,
  duration: 0.22,
}

const MemberLayoutContent = () => {
  const [appState] = useApp()
  const location = useLocation()
  const { customerId, tenantSource } = useTenant()

  const { needsChoice } = useCompanySelection()
  const { invitations, hasInvitations, dismissInvitations } =
    useCompanyInvitations()

  const showCustomerBanner =
    Boolean(customerId) && tenantSource !== 'membership'
  const [authState] = useAuth()
  const {
    isFeedbackEnabled,
    isFeedbackOpen,
    feedbackPrefill,
    closeFeedback,
    toggleFeedback,
  } = useFeedback()
  const isUserAnalyticsEnabled = useFeatureFlag(FLAGS.USER_ANALYTICS)
  const knowledgeBase = useKnowledgeBase()
  const knowledgeBaseDockedWidth = useKnowledgeBaseDockedWidth()

  const shouldShowAnalyticsBox = useMemo(
    () =>
      authState.user &&
      appState.appInfo?.analytics === null &&
      isUserAnalyticsEnabled,
    [appState.appInfo, isUserAnalyticsEnabled, authState.user]
  )

  const isTicketsPage = useMemo(
    () => location.pathname.startsWith('/tickets'),
    [location.pathname]
  )

  const isSettingsPage = useMemo(
    () => location.pathname.startsWith('/settings'),
    [location.pathname]
  )

  const shouldShowTfaEnforcementBanner = useMemo(
    () => authState.user && appState.appInfo?.enforce_tfa === true,
    [authState.user, appState.appInfo]
  )

  const tfaEnforcementAllowSkip = useMemo(
    () => appState.appInfo?.tfa_enforcement_allow_skip === true,
    [appState.appInfo]
  )

  const tfaEnforcementType = useMemo(
    () => appState.appInfo?.tfa_enforcement_type ?? 'none',
    [appState.appInfo]
  )

  // Impersonating a customer must not change the navigation: the chrome stays
  // the same and CustomerBanner is the only impersonation affordance.
  const { isSidebar: useSidebar } = useNavLayout()

  const animateKey = useMemo(() => {
    if (isTicketsPage) return 'tickets'
    if (isSettingsPage) return 'settings'
    return location.pathname
  }, [isTicketsPage, isSettingsPage, location.pathname])

  if (shouldShowTfaEnforcementBanner) {
    return (
      <div className="flex flex-col min-h-full w-full bg-radial from-accent-60 to-accent-90">
        <TwoFactorEnforcementDialog
          isOpen
          allowSkip={tfaEnforcementAllowSkip}
          tfaEnforcementType={tfaEnforcementType}
        />
      </div>
    )
  }

  return (
    <LazyMotion features={domAnimation} strict>
      <div
        className={cn(
          'page-container w-full',
          isTicketsPage ? 'min-h-full xl:h-screen' : 'min-h-full',
          useSidebar ? 'flex flex-col md:flex-row' : 'flex flex-col',
          shouldShowTfaEnforcementBanner ? 'bg-surface' : 'bg-canvas'
        )}
        id="member-layout"
        data-cy="member-layout"
        style={
          knowledgeBase.isEnabled
            ? {
                paddingRight: knowledgeBaseDockedWidth,
                transition: 'padding-right 0.3s cubic-bezier(0.32, 0.72, 0, 1)',
              }
            : undefined
        }
      >
        {useSidebar && <Sidebar />}

        <div
          className={cn(
            'flex min-w-0 flex-1 flex-col',
            isTicketsPage ? 'xl:min-h-0' : 'min-h-screen'
          )}
        >
          {hasInvitations ? (
            <CompanyInvitations
              invitations={invitations}
              onDismiss={dismissInvitations}
            />
          ) : (
            needsChoice && <CompanyChooser />
          )}
          {showCustomerBanner && <CustomerBanner />}
          {!useSidebar && <Navigation />}

          <div className="flex min-h-0 flex-1">
            <main className="flex min-h-0 min-w-0 flex-1 flex-col overflow-x-hidden">
              {appState.appInfo &&
                typeof appState.appInfo.email_verified === 'boolean' &&
                appState.appInfo.email_verified === false && (
                  <EmailVerificationBanner />
                )}

              <m.div
                key={animateKey}
                initial="initial"
                animate="in"
                exit="out"
                variants={pageVariants}
                transition={pageTransition}
                className="flex min-h-0 flex-1 flex-col"
              >
                <Outlet />
              </m.div>
            </main>
          </div>

          <Footer className="page-shell" />
        </div>

        {shouldShowAnalyticsBox && (
          <m.div variants={pageVariants}>
            <AnalyticsBox />
          </m.div>
        )}

        {createPortal(
          <Suspense>
            <AddService />
          </Suspense>,
          document.body
        )}

        <KnowledgeBasePanel />

        {isFeedbackEnabled && (
          <>
            <button
              className={cn(
                'fixed right-3 bottom-[max(0.5rem,env(safe-area-inset-bottom))] z-50 flex max-md:hidden md:size-12 rounded-full items-center justify-center bg-accent-60 shadow-raised transition-all hover:bg-accent-100 hover:scale-105 sm:right-3 right-0',
                isFeedbackOpen
                  ? 'sm:-translate-x-40 -translate-x-[43vw]'
                  : 'translate-x-0'
              )}
              onClick={toggleFeedback}
              style={
                knowledgeBaseDockedWidth
                  ? { right: knowledgeBaseDockedWidth + 12 }
                  : undefined
              }
              aria-label={isFeedbackOpen ? 'Close feedback' : 'Give feedback'}
            >
              {isFeedbackOpen ? (
                <XMarkIcon className="size-6 text-white" />
              ) : (
                <FeedbackIcon className="size-6 text-white" />
              )}
            </button>
            <FeedbackPanel
              isOpen={isFeedbackOpen}
              onClose={closeFeedback}
              prefill={feedbackPrefill}
            />
          </>
        )}
      </div>
    </LazyMotion>
  )
}

const MemberLayout = () => (
  <FeedbackProvider>
    <KnowledgeBaseProvider>
      <MemberLayoutContent />
    </KnowledgeBaseProvider>
  </FeedbackProvider>
)

export default MemberLayout
